import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileIcon } from 'lucide-react';

interface FileUploaderProps {
  onUpload: (files: File[], onProgress: (file: File, progress: number) => void) => Promise<void>;
  isUploading: boolean;
}

interface FileWithPreview extends File {
  preview: string;
  progress: number;
  id: string;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onUpload, isUploading }) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles.map(file => Object.assign(file, {
      preview: URL.createObjectURL(file),
      progress: 0,
      id: `${file.name}-${Date.now()}`
    })));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const removeFile = (file: File) => {
    setFiles(files => files.filter(f => f !== file));
  };

  const handleUpload = async () => {
    try {
      await onUpload(files, (file: File, progress: number) => {
        setFiles(prevFiles =>
          prevFiles.map(f =>
            f.name === file.name ? { ...f, progress } : f
          )
        );
      });
      // Clear files and reset progress after successful upload
      setFiles([]);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const getFilePreview = (file: FileWithPreview) => {
    if (file.type?.startsWith('image/')) {
      return <img src={file.preview} alt={file.name} className="w-10 h-10 object-cover mr-2 rounded" />;
    }
    return <FileIcon className="w-10 h-10 mr-2 text-neutral-400" />;
  };

  return (
    <div className="bg-white rounded-xl">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl transition-colors duration-200 cursor-pointer 
          ${isDragActive ? 'border-blue-500 bg-primary-50' : 'border-neutral-300'}`}
      >
        <div className="px-6 py-8 text-center">
          <input {...getInputProps()} />
          <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {isDragActive ? 'Drop files here...' : 'Drag & drop files or click to select'}
          </p>
        </div>
      </div>

      {files.length > 0 && (
        <div className="border-t mt-4">
          <div className="pt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-neutral-700">Selected Files</h3>
              <button
                onClick={handleUpload}
                disabled={isUploading || files.length === 0}
                className="bg-primary-500 text-white px-4 py-2 text-sm rounded-md hover:bg-primary-600 disabled:bg-neutral-400 disabled:cursor-not-allowed"
              >
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
            <ul className="space-y-3">
              {files.map((file) => (
                <li key={file.id} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      {getFilePreview(file)}
                      <span className="text-sm text-neutral-700 truncate">{file.name}</span>
                    </div>
                    <button 
                      onClick={() => removeFile(file)} 
                      className="text-neutral-400 hover:text-red-500"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  {file.progress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
